<template>
    <!-- @vue-generic {BlastForecastItemAggregated} -->
    <vx-chart-calendar
        data-test="vx-chart-calendar"
        :data="data"
        :valueKey="props.estimatedCost ? 'estimated_cost' : 'num_messages'"
        :loading="loading"
        :error="Boolean(error)"
        legendLabel="Messages"
        :timezone="props.timezone"
        :initialPage="calendarMinPage"
        :minPage="calendarMinPage"
        :maxPage="calendarMaxPage"
    >
        <template v-slot:title>{{ props.estimatedCost ? 'Estimated Cost by Day' : 'Messages by Day' }}</template>

        <template v-slot:dayContent="{ day, item }">
            <vx-popover
                v-if="item && (item.num_messages || item.estimated_cost)"
                teleport
                hover
                arrow
                placement="top"
                class="grow flex! [&>div]:grow [&>div]:flex"
            >
                <div
                    @click="emit('dayClick', item)"
                    class="grow flex flex-col justify-between px-0.5 @min-[48px]/day-content:py-1 @min-[48px]/day-content:px-1.5"
                    :class="{
                        'cursor-pointer': item,
                    }"
                >
                    <div class="flex justify-between items-center">
                        <div
                            :class="[
                                day.isToday && day.inMonth && item?.intensity
                                    ? ['700', '900'].includes(item?.intensity)
                                        ? 'text-white'
                                        : 'text-sky-950'
                                    : '',
                                {
                                    'font-bold': day.isToday && day.inMonth,
                                },
                            ]"
                        >
                            {{ day.isToday && day.inMonth ? 'Today' : vxFormatOrdinal(day.day) }}
                        </div>
                        <div v-if="FEATURES.contact_groups">
                            <font-awesome-icon
                                v-if="item && item.num_corporate > 0 && day.inMonth"
                                class="text-xs"
                                :class="{
                                    'text-sky-300':
                                        (props.estimatedCost ? item?.cost_intensity : item?.intensity) !== 'default' &&
                                        (props.estimatedCost ? item?.cost_intensity : item?.intensity) !== 'past',
                                }"
                                :icon="faCrown"
                            />
                        </div>
                    </div>
                    <div v-if="day.inMonth && item" class="flex items-center">
                        <font-awesome-icon
                            v-if="props.estimatedCost"
                            widthAuto
                            class="text-[10px] mr-px"
                            :icon="faDollar"
                        />

                        <span class="font-bold text-[8px] @min-[48px]/day-content:text-[10px] mr-1">
                            {{
                                props.estimatedCost
                                    ? vxFormatCentsAsDollars(item.estimated_cost, false)
                                    : vxFormatCount(item.num_messages)
                            }}
                        </span>

                        <font-awesome-icon
                            v-if="!props.estimatedCost"
                            class="text-[10px] hidden! @min-[48px]/day-content:inline-block!"
                            :icon="faMessage"
                        />
                    </div>
                </div>

                <template v-if="item" v-slot:content>
                    <div class="w-48 text-sm flex flex-col gap-2">
                        <div class="font-bold">
                            {{ dayjs.utc(day.date).tz(getClientTz()).format('MMM Do, YYYY') }}
                        </div>
                        <div v-if="FEATURES.contact_groups" class="flex gap-1">
                            <meta-badge v-if="item.num_corporate > 0" :icon="faCrown" color="amber">
                                {{ item.num_corporate }}
                            </meta-badge>
                            <meta-badge v-if="item.num_org_units > 0" :icon="faBuildingFlag" color="violet">
                                {{ item.num_org_units }}
                            </meta-badge>
                            <meta-badge v-if="item.num_groups > 0" :icon="faGrid2Plus" color="slate">
                                {{ item.num_groups }}
                            </meta-badge>
                        </div>
                        <div class="text-xs flex flex-col gap-1">
                            <div class="flex justify-between items-center">
                                <div class="text-slate-500">Blasts</div>
                                <div>{{ vxFormatCount(item.num_blasts) }}</div>
                            </div>
                            <div class="flex justify-between items-center">
                                <div class="text-slate-500">Estimated Messages:</div>
                                <div>{{ vxFormatCount(item.num_messages) }}</div>
                            </div>
                            <div v-if="showEstimatedCost" class="flex justify-between items-center">
                                <div class="text-slate-500">Estimated Cost:</div>
                                <div>{{ vxFormatCentsAsDollars(item.estimated_cost, true) }}</div>
                            </div>
                        </div>
                    </div>
                </template>
            </vx-popover>
        </template>

        <template v-slot:footerRight>
            <vx-button data-test="explain-button" color="transparent" size="xs" @click="explainModal = true">
                <font-awesome-icon widthAuto :icon="faCircleQuestion" />
            </vx-button>

            <vx-modal data-test="explain-modal" size="md" v-model:visible="explainModal" :padding="false">
                <template v-slot:header>
                    <div class="p-6 bg-white border-t-0 border-x-0 border-b border-solid border-b-slate-100">
                        Blast Forecast Calendar
                    </div>
                </template>

                <div class="p-6 bg-slate-50 [&_p_strong]:text-slate-700 [&_p]:mb-6 lg:py-10 lg:px-12">
                    <p>
                        <strong>Color Gradient</strong> <br />
                        Shows the % of your total reachable audience scheduled to receive messages that day. Darker
                        colors = higher % of audience reached.
                    </p>

                    <p>
                        <strong>Messages (#)</strong> <br />
                        Total number of messages planned across all entities (Corporate, Holding Groups,
                        Groups/Locations).
                    </p>

                    <div v-if="showEstimatedCost">
                        <strong class="text-slate-700">Estimate Costs ($)</strong>
                        <p>This is only an estimate, as the audience segment can vary in size prior to sending.</p>
                        <div class="mb-6">
                            <span>Estimated Costs are calculated using:</span>
                            <ul class="pl-6 list-disc">
                                <li>Message Type: SMS or MMS</li>
                                <li>Quantity of SMS Segments (message length)</li>
                                <li>Contracted rates (SMS or MMS rate + Carrier Fee)</li>
                                <li>Quantity of contacts within the audience at time of estimation</li>
                            </ul>
                        </div>
                        <p>
                            Messages that contain variables can also cause the estimate to diverge from the actual cost
                            at send time.
                        </p>
                    </div>

                    <p><strong>When clicking a day</strong> <br /></p>

                    <p v-if="FEATURES.contact_groups">
                        <strong
                            ><font-awesome-icon class="text-amber-400" :icon="faCrown"></font-awesome-icon>
                            Blasts</strong
                        >
                        <br />
                        Number of campaigns scheduled by Corporate (Franchisor).
                    </p>

                    <p v-if="FEATURES.contact_groups">
                        <strong>
                            <font-awesome-icon class="text-violet-500" :icon="faBuildingFlag"></font-awesome-icon>
                            Holding Group Blasts
                        </strong>
                        <br />
                        Number of campaigns scheduled by Holding Groups (entities that own multiple
                        franchisees/locations).
                    </p>

                    <p v-if="FEATURES.contact_groups">
                        <strong
                            ><font-awesome-icon class="text-slate-500" :icon="faGrid2Plus"></font-awesome-icon> Group
                            Blasts</strong
                        >
                        <br />
                        Number of campaigns scheduled by individual Groups/Locations.
                    </p>

                    <p>
                        <strong>Total Blasts</strong> <br />
                        Sum of all campaigns across Corporate, Holding Groups, and Groups.
                    </p>

                    <p>
                        <strong>Total Messages</strong> <br />
                        Overall messages planned to be sent that day (audience size × # of messages per blast).
                    </p>
                </div>
            </vx-modal>
        </template>
    </vx-chart-calendar>
</template>

<script setup lang="ts">
import { faBuildingFlag, faCrown, faDollar, faGrid2Plus } from '@fortawesome/pro-solid-svg-icons';
import { faCircleQuestion, faMessage } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxChartCalendar,
    vxFormatCentsAsDollars,
    vxFormatCount,
    vxFormatOrdinal,
    VxPopover,
    VxModal,
} from '@voxie/frontend-components';
import { computed, ref, watch, type PropType, inject } from 'vue';
import type { GetBlastForecastRequest } from '~/services/blastForecast/blastForecastClient.types';
import dayjsLib from '~/utils/dayjs';
import MetaBadge from '~/components/general/MetaBadge.vue';
import { getClientTz } from '~/utils/date';
import { useBlastForecastSummary, type BlastForecastItemAggregated } from '../composables/useBlastForecastSummary';
import type { Features } from '~/provides/features.types';

const dayjs = dayjsLib;
const teamId = Spark.state.currentTeam.id;

const FEATURES = inject('FEATURES') as Features;

const props = defineProps({
    fetchParams: {
        type: Object as PropType<GetBlastForecastRequest>,
        required: true,
    },
    timezone: String,
    orgUnitId: Number,
    showEstimatedCost: {
        type: Boolean,
        required: true,
    },
    estimatedCost: Boolean,
});

const emit = defineEmits<{ (e: 'dayClick', item: BlastForecastItemAggregated): void }>();

const explainModal = ref(false);
const { data, loading, error, fetchData } = useBlastForecastSummary();

const calendarMinPage = computed(() => {
    const date = dayjs.utc(props.fetchParams.range[0]).tz(getClientTz());
    return {
        year: date.year(),
        month: date.month() + 1,
    };
});

const calendarMaxPage = computed(() => {
    const date = dayjs.utc(props.fetchParams.range[1]).tz(getClientTz());
    return {
        year: date.year(),
        month: date.month() + 1,
    };
});

watch(
    [() => props.fetchParams],
    () => {
        fetchData({ teamId: teamId, orgUnitId: props.orgUnitId }, props.fetchParams);
    },
    { immediate: true, deep: true }
);
</script>
